import { useMemo } from 'react'
import { parseDelimitedTable } from '@/utils/csvPreview'

interface Props {
  text: string
}

function looksNumeric(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed) return false
  const normalized = trimmed.replace(/[$,\s]/g, '')
  if (!normalized || normalized === '-') return false
  return !Number.isNaN(Number(normalized))
}

export default function CsvPreviewTable({ text }: Props) {
  const table = useMemo(() => parseDelimitedTable(text), [text])

  if (!table) {
    return (
      <div className="flex flex-1 items-center justify-center p-6 text-sm text-gray-500">
        Could not parse CSV content for table view.
      </div>
    )
  }

  const numericColumns = table.headers.map((_, colIdx) =>
    table.rows.length > 0 &&
    table.rows.every((row) => {
      const cell = row[colIdx] ?? ''
      return !cell.trim() || looksNumeric(cell)
    })
  )

  return (
    <div className="flex-1 min-h-0 overflow-auto">
      <table className="min-w-full border-collapse text-xs">
        <thead className="sticky top-0 z-10 bg-gray-100 shadow-[0_1px_0_0_rgb(229,231,235)]">
          <tr>
            <th
              scope="col"
              className="sticky left-0 z-20 w-10 min-w-[2.5rem] border-b border-r border-gray-200 bg-gray-100 px-2 py-2 text-center font-medium text-gray-500"
            >
              #
            </th>
            {table.headers.map((header, idx) => (
              <th
                key={`${header}-${idx}`}
                scope="col"
                className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-800 whitespace-nowrap"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, rowIdx) => (
            <tr
              key={rowIdx}
              className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/80'}
            >
              <td className="sticky left-0 z-[1] border-r border-gray-200 bg-inherit px-2 py-1.5 text-center tabular-nums text-gray-400">
                {rowIdx + 1}
              </td>
              {table.headers.map((_, colIdx) => {
                const value = row[colIdx] ?? ''
                const empty = !value.trim()
                return (
                  <td
                    key={colIdx}
                    className={`border-b border-gray-100 px-3 py-1.5 whitespace-nowrap max-w-[280px] truncate ${
                      numericColumns[colIdx] ? 'text-right tabular-nums text-gray-900' : 'text-left text-gray-800'
                    } ${empty ? 'text-gray-300 italic' : ''}`}
                    title={value || undefined}
                  >
                    {empty ? '—' : value}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {table.rows.length === 0 && (
        <p className="p-4 text-sm text-gray-500">No data rows in this file.</p>
      )}
    </div>
  )
}
